import React, { useCallback, useEffect, useRef } from "react";
import { HubConnectionBuilder, HttpTransportType } from "@microsoft/signalr";
import { v4 } from "uuid";
import { LocalStorageService } from "../service/local-storage-service";
import { SessionService } from "../service/session-service";

const defaultValue = {
    hubContext: null,
    connected: false,
    onUpdateStudyGrid: {},
    // Bildirim olayları. `key` her olayda değişir; dinleyenler useEffect bağımlılığı olarak kullanır.
    onNotification: {},
    // Yeniden bağlanma sonrası: kesinti sırasında kaçan olaylar için tam tazeleme sinyali.
    onReconnected: {},
}

export const SignalRActions = {
    ON_UPDATE_STUDY_GRID: 'ON_UPDATE_STUDY_GRID',
    ON_NOTIFICATION: 'ON_NOTIFICATION',
    ON_RECONNECTED: 'ON_RECONNECTED',
    SET_HUB_CONTEXT: 'SET_HUB_CONTEXT',
    SET_CONNECTED: 'SET_CONNECTED',
}

const SignalRContext = React.createContext(defaultValue)

function signalRReducer(state, action) {
    switch (action.type) {
        case SignalRActions.ON_UPDATE_STUDY_GRID: {
            return {
                ...state,
                onUpdateStudyGrid: { key: v4(), data: action.data }
            }
        }
        case SignalRActions.ON_NOTIFICATION: {
            return {
                ...state,
                onNotification: { key: v4(), event: action.event, payload: action.payload }
            }
        }
        case SignalRActions.ON_RECONNECTED: {
            return {
                ...state,
                onReconnected: { key: v4() }
            }
        }
        case SignalRActions.SET_HUB_CONTEXT: {
            return { ...state, hubContext: action.hubContext }
        }
        case SignalRActions.SET_CONNECTED: {
            return { ...state, connected: action.connected }
        }
        default: {
            return defaultValue
        }
    }
}

// Sunucudaki SignalRNotificationPublisher sabitleriyle birebir.
const NOTIFICATION_EVENTS = ['NotificationChanged', 'NotificationRemoved', 'NotificationCleared'];

function SignalRProvider({ children }) {
    const [signalR, dispatch] = React.useReducer(signalRReducer, defaultValue);

    // Bağlantı ref'te tutulur: temizlik fonksiyonunun ilk render'daki (null) değeri yakalayıp
    // bağlantıyı hiç kapatmaması eski hataydı.
    const connectionRef = useRef(null);

    const build = useCallback(() => {
        // Hub kimlik istiyor. Çerezli oturumda handshake'i oturum çerezi taşır (hub aynı origin);
        // elde eski bir token varsa eskisi gibi query string ile gider (sunucuda
        // JwtBearerEvents.OnMessageReceived önce query'e, o yoksa çereze bakıyor).
        // accessTokenFactory boş dönerse SignalR access_token parametresini hiç eklemez.
        const connection = new HubConnectionBuilder()
            .withUrl(`${window.AppSettings.server.api}/api/notification-hub`, {
                accessTokenFactory: () => LocalStorageService.getToken(),
                transport: HttpTransportType.WebSockets | HttpTransportType.LongPolling,
            })
            // Ağ kesintisinde bağlantı sessizce ölüp bir daha kurulmuyordu.
            .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
            .build();

        connection.onreconnected(() => {
            dispatch({ type: SignalRActions.SET_CONNECTED, connected: true });
            // Kesinti sırasında yayınlanan olaylar kaybolur (SignalR teslim garantisi vermez):
            // dinleyiciler bu sinyalle durumlarını sunucudan baştan çeker.
            dispatch({ type: SignalRActions.ON_RECONNECTED });
        });

        connection.onreconnecting(() => {
            dispatch({ type: SignalRActions.SET_CONNECTED, connected: false });
        });

        connection.onclose(() => {
            dispatch({ type: SignalRActions.SET_CONNECTED, connected: false });
        });

        connection.on('UpdateStudyGrid', (...args) => {
            dispatch({ type: SignalRActions.ON_UPDATE_STUDY_GRID, data: args });
        });

        NOTIFICATION_EVENTS.forEach(eventName => {
            connection.on(eventName, (payload) => {
                dispatch({ type: SignalRActions.ON_NOTIFICATION, event: eventName, payload });
            });
        });

        return connection;
    }, []);

    useEffect(() => {
        // Oturum yoksa bağlanmayı denemenin anlamı yok (hub [Authorize]).
        if (!SessionService.isAuthenticated())
            return;

        let cancelled = false;
        let retryTimer = null;

        const connect = async () => {
            if (cancelled || connectionRef.current)
                return;

            const connection = build();

            try {
                await connection.start();

                if (cancelled) {
                    connection.stop();
                    return;
                }

                connectionRef.current = connection;
                dispatch({ type: SignalRActions.SET_HUB_CONTEXT, hubContext: connection });
                dispatch({ type: SignalRActions.SET_CONNECTED, connected: true });
            } catch (exp) {
                // İLK bağlantı başarısızsa withAutomaticReconnect devreye GİRMEZ; kendimiz deneriz.
                console.warn('[SignalR] connection failed, retrying in 5s', exp);
                retryTimer = setTimeout(connect, 5000);
            }
        }

        connect();

        return () => {
            cancelled = true;
            clearTimeout(retryTimer);

            const connection = connectionRef.current;
            connectionRef.current = null;
            connection?.stop();
        }
        // Tek sefer kurulur: bağımlılığa hubContext koymak, kurulan bağlantıyı temizlikte
        // hemen kapatan bir döngü yaratıyordu.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <SignalRContext.Provider value={{ signalR, dispatch }}>
            {children}
        </SignalRContext.Provider>
    )
}

function useSignalR() {
    const context = React.useContext(SignalRContext);

    if (!context)
        throw new Error('SignalR Provider Error')

    const { signalR } = context;

    return {
        onUpdateStudyGrid: signalR.onUpdateStudyGrid,
        onNotification: signalR.onNotification,
        onReconnected: signalR.onReconnected,
        connected: signalR.connected,
        hubContext: signalR.hubContext,
    }
}

export { SignalRProvider, useSignalR }
